/**
 * Sage Code Matcher
 *
 * Upload a Sage staff list, match Long Description titles to Role Master entries,
 * preview matches, and apply Sage title codes.
 */

import React, { useState } from 'react';
import { Upload, CheckCircle, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiClient } from '@/services/apiClient';

interface SageMatch {
  role_master_entry_id: number;
  role_code: string;
  title: string;
  current_sage_code: string | null;
  matched_sage_code: string | null;
  match_type: string | null;
  confidence: number;
}

interface MatchPreview {
  total_rm_entries: number;
  total_sage_codes: number;
  matched: number;
  unmatched: number;
  matches: SageMatch[];
}

const API = '/api/v1/identity/org/role-master/sage-codes';

const SageCodeMatcher: React.FC = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<MatchPreview | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ applied: number } | null>(null);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setPreview(null);
    setApplyResult(null);
    setExcluded(new Set());

    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await apiClient.post<MatchPreview>(`${API}/match`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(resp.data);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to match Sage codes');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const toggleExclude = (id: number) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const matchedEntries = preview?.matches.filter(m => m.matched_sage_code) || [];
  const includedEntries = matchedEntries.filter(m => !excluded.has(m.role_master_entry_id));

  const handleApply = async () => {
    if (!preview) return;
    setIsApplying(true);
    try {
      const codes = includedEntries.map(m => ({
        role_master_entry_id: m.role_master_entry_id,
        sage_code: m.matched_sage_code,
      }));
      const resp = await apiClient.post<{ applied: number }>(`${API}/apply`, codes);
      setApplyResult(resp.data);
      setPreview(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to apply Sage codes');
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <label className="cursor-pointer">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleUpload}
            className="hidden"
            disabled={isUploading}
          />
          <div className="flex items-center gap-2 px-4 py-2 bg-heritage-blue text-white rounded-md hover:bg-heritage-blue/90 transition-colors text-sm font-medium">
            <Upload className="h-4 w-4" />
            {isUploading ? 'Matching...' : 'Upload Sage Staff List'}
          </div>
        </label>
      </div>

      {preview && (
        <div className="space-y-4 border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Sage Code Match Preview</h3>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">RM Entries:</span> <strong>{preview.total_rm_entries}</strong></div>
            <div><span className="text-gray-500">Sage Codes:</span> <strong>{preview.total_sage_codes}</strong></div>
            <div><span className="text-emerald-600">Matched:</span> <strong>{preview.matched}</strong></div>
            <div><span className="text-amber-600">Unmatched:</span> <strong>{preview.unmatched}</strong></div>
          </div>

          {matchedEntries.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={excluded.size === 0}
                  onChange={() => {
                    if (excluded.size === 0) {
                      setExcluded(new Set(matchedEntries.map(m => m.role_master_entry_id)));
                    } else {
                      setExcluded(new Set());
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-600">Select all ({matchedEntries.length} matches)</span>
                {excluded.size > 0 && (
                  <span className="text-gray-400 ml-2">{excluded.size} excluded</span>
                )}
              </div>

              <div className="max-h-[400px] overflow-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="whitespace-nowrap">Code</TableHead>
                      <TableHead className="whitespace-nowrap">RM Title</TableHead>
                      <TableHead className="whitespace-nowrap">Sage Code</TableHead>
                      <TableHead className="whitespace-nowrap">Match</TableHead>
                      <TableHead className="whitespace-nowrap">Confidence</TableHead>
                      <TableHead className="whitespace-nowrap">Current</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matchedEntries.map(m => (
                      <TableRow key={m.role_master_entry_id} className={excluded.has(m.role_master_entry_id) ? 'opacity-40' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={!excluded.has(m.role_master_entry_id)}
                            onChange={() => toggleExclude(m.role_master_entry_id)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{m.role_code}</TableCell>
                        <TableCell className="whitespace-nowrap">{m.title}</TableCell>
                        <TableCell className="font-mono text-xs font-semibold whitespace-nowrap">{m.matched_sage_code}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            m.match_type === 'exact' ? 'text-emerald-600 border-emerald-300' :
                            m.match_type === 'suffix_stripped' ? 'text-blue-600 border-blue-300' :
                            'text-amber-600 border-amber-300'
                          }>
                            {m.match_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{Math.round(m.confidence * 100)}%</TableCell>
                        <TableCell className="text-gray-400 whitespace-nowrap">{m.current_sage_code || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={handleApply} disabled={isApplying || includedEntries.length === 0}>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {isApplying ? 'Applying...' : `Apply ${includedEntries.length} Sage Codes`}
                </Button>
                <Button variant="ghost" onClick={() => setPreview(null)}>Cancel</Button>
              </div>
            </>
          )}

          {matchedEntries.length === 0 && (
            <div className="flex items-center gap-2 text-amber-600 text-sm">
              <AlertTriangle className="h-4 w-4" />
              No matches found between the Sage staff list titles and Role Master entries.
            </div>
          )}
        </div>
      )}

      {applyResult && (
        <div className="border rounded-lg p-4 bg-emerald-50">
          <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" /> Sage Codes Applied
          </h3>
          <p className="text-sm mt-2">{applyResult.applied} Role Master entries updated with Sage title codes.</p>
        </div>
      )}
    </div>
  );
};

export default SageCodeMatcher;
